import { resolveWithDelay } from "@core/async/promises";
import { DependencyList, useCallback, useEffect, useMemo, useRef, useState } from "react";

export type AsyncOperationResult<T, P extends unknown[]> = [
	(...parameters: P) => Promise<T | undefined>,
	boolean,
	boolean,
];

export interface AsyncOperationOptions {
	minimumDuration?: number;
	deps?: DependencyList;
	rethrow?: boolean;
	initialLoadingState?: boolean;
	completionCallback?: () => void;
}

export function useAsyncOperation<T, P extends unknown[]>(
	operation: (...parameters: P) => Promise<T>,
	options: AsyncOperationOptions,
): AsyncOperationResult<T, P> {
	const [loading, setLoading] = useState(options.initialLoadingState ?? false);
	const [error, setError] = useState(false);
	const isCancelled = useRef(false);

	const minimumDuration = useMemo(() => options.minimumDuration ?? false, [options.minimumDuration]);
	const deps = useMemo((): DependencyList => options.deps ?? [], [options.deps]);
	const doRethrow = useMemo(() => options.rethrow ?? false, [options.rethrow]);

	useEffect(
		() => () => {
			// Cancel operation on hook dismount
			isCancelled.current = true;
		},
		[],
	);

	const operationWrapper = useCallback(
		async (...parameters: P) => {
			try {
				setError(false);
				setLoading(true);
				const result = minimumDuration
					? await resolveWithDelay(operation(...parameters), minimumDuration)
					: await operation(...parameters);
				if (!isCancelled.current) {
					options.completionCallback?.();
					setError(false);
				}
				return result;
			} catch (e) {
				if (!isCancelled.current) {
					setError(true);
				}
				if (doRethrow) {
					throw e;
				}
			} finally {
				if (!isCancelled.current) {
					setLoading(false);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[deps, operation, minimumDuration, doRethrow],
	);

	return [operationWrapper, loading, error];
}
